import { useCallback, useState } from 'react';

import { QUERY_STATE } from '../../constants';
import { epsiDocsSynchronizeCreate } from '../../openapi/operations/epsiDocsSynchronizeCreate';

const HTTP_ACCEPTED = 202;
const HTTP_CONFLICT = 409;
const HTTP_FORBIDDEN = 403;

export type UseEpsiSynchronize = {
  synchronize(): Promise<void>;
  queryState: QUERY_STATE;
  /** HTTP status posledního pokusu, `null` dokud se neklikne. */
  status: number | null;
  createdCount: number | null;
};

/**
 * Ruční spuštění R46 synchronizace PSPI.
 *
 * Odpověď nenese výsledek běhu — 202 znamená jen „spuštěno na pozadí“, průběh
 * se dál sleduje přes stavy jednotlivých dnů v přehledu. Nese ale `created`,
 * tedy seznam nově založených dnů; prázdný znamená, že stavová data už byla
 * aktuální a stahovat se nic nebude.
 * 409 není chyba, jen informace, že stahování téhož zdroje už běží.
 */
export const useEpsiSynchronize = (onStarted?: () => void): UseEpsiSynchronize => {
  const [queryState, setQueryState] = useState<QUERY_STATE>(QUERY_STATE.EMPTY);
  const [status, setStatus] = useState<number | null>(null);
  const [createdCount, setCreatedCount] = useState<number | null>(null);

  const synchronize = useCallback(async () => {
    setQueryState(QUERY_STATE.WAITING);
    setCreatedCount(null);

    // Prázdné tělo = běžná změnová synchronizace. `stavova`/`datum_od` je
    // jednorázový admin scénář při zřizování a v běžném UI se neexponuje.
    const response = await epsiDocsSynchronizeCreate({ request: {} });

    setStatus(response.status);
    setQueryState(response.status === HTTP_ACCEPTED ? QUERY_STATE.SUCCESS : QUERY_STATE.FAIL);

    const created = response.data?.created;
    setCreatedCount(Array.isArray(created) ? created.length : null);

    if (response.status === HTTP_ACCEPTED) {
      onStarted?.();
    }
  }, [onStarted]);

  return { synchronize, queryState, status, createdCount };
};

export const isEpsiSynchronizeAccepted = (status: number | null) => status === HTTP_ACCEPTED;
export const isEpsiSynchronizeConflict = (status: number | null) => status === HTTP_CONFLICT;
export const isEpsiSynchronizeForbidden = (status: number | null) => status === HTTP_FORBIDDEN;
